export const GROCERY_PLATFORM_ORDER = ["tesco", "asda", "morrison", "sainsbury"];

function normalizeGroceryProductPlatform(platform) {
  const p = String(platform || "").trim().toLowerCase();
  if (!p) return "";
  if (p.includes("tesco")) return "tesco";
  if (p.includes("asda")) return "asda";
  if (p.includes("morris")) return "morrison";
  if (p.includes("sains")) return "sainsbury";
  return "";
}

function parsePrice(value) {
  if (value == null || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const normalized = String(value).trim().replace(",", ".").replace(/[^0-9.-]/g, "");
  if (!normalized) return null;
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseReviewCount(value) {
  if (value == null || value === "") return null;
  const parsed = Number.parseInt(String(value).replace(/[^0-9]/g, ""), 10);
  return Number.isFinite(parsed) ? parsed : null;
}

function pickVisitUrl(item) {
  return (
    item?.url ||
    item?.product_url ||
    item?.productUrl ||
    item?.store_url ||
    item?.storeUrl ||
    item?.visit_url ||
    item?.visitUrl ||
    item?.link ||
    ""
  );
}

function comparisonPlatformKey(item) {
  return normalizeGroceryProductPlatform(
    item?.platform || item?.retailer || item?.provider || item?.store || item?.site
  );
}

/** Convert stored grocery `platform_data` into ProductDetail `comparison` rows. */
export function platformDataToComparison(platformData) {
  const rows = Array.isArray(platformData) ? platformData : [];
  return rows
    .map((item) => {
      const platform = comparisonPlatformKey(item);
      if (!platform) return null;
      const basePrice = parsePrice(item?.price);
      const offerPrice = parsePrice(item?.offer_price ?? item?.offerPrice);
      const price =
        Number.isFinite(basePrice) && basePrice > 0
          ? basePrice
          : Number.isFinite(offerPrice) && offerPrice > 0
            ? offerPrice
            : null;
      const rating = parsePrice(
        item?.rating ?? item?.average_rating ?? item?.avg_rating
      );
      const reviewCount = parseReviewCount(
        item?.review_count ?? item?.reviews ?? item?.reviewCount
      );
      return {
        platform,
        product_id: String(item?.product_id ?? item?.pid ?? item?.sku ?? "").trim(),
        price,
        offer_price: Number.isFinite(offerPrice) && offerPrice > 0 ? offerPrice : null,
        url: String(pickVisitUrl(item) || "").trim() || null,
        product_name: String(item?.product_name ?? item?.title ?? "").trim(),
        match_type: String(item?.match_type ?? item?.matchType ?? "").trim(),
        average_rating: Number.isFinite(rating) ? rating : null,
        rating: Number.isFinite(rating) ? rating : null,
        review_count: Number.isFinite(reviewCount) ? reviewCount : null,
        brand: String(item?.brand ?? "").trim(),
        description: String(item?.description ?? "").trim(),
      };
    })
    .filter(Boolean);
}

export function hasGroceryPlatformData(platformData) {
  return platformDataToComparison(platformData).some(
    (row) =>
      (Number.isFinite(row?.price) && row.price > 0) ||
      (Number.isFinite(row?.offer_price) && row.offer_price > 0)
  );
}

export function buildGroceryPlatformData(product) {
  const comparison = Array.isArray(product?.comparison) ? product.comparison : [];
  const byPlatform = {};

  comparison.forEach((item) => {
    const platform = comparisonPlatformKey(item);
    if (!platform) return;
    byPlatform[platform] = item;
  });

  return GROCERY_PLATFORM_ORDER.map((platform) => {
    const item = byPlatform[platform] || {};
    const price = parsePrice(item?.price ?? item?.unit_price ?? item?.price_per_unit);
    const offerPrice =
      parsePrice(item?.offer_price) ??
      parsePrice(item?.loyalty_pricing) ??
      (() => {
        const offers = Array.isArray(item?.offers) ? item.offers : [];
        return parsePrice(offers[0]?.offer_price ?? offers[0]?.price);
      })();
    const rating = parsePrice(
      item?.rating ?? item?.average_rating ?? item?.avg_rating ?? item?.review_rating
    );
    const reviewCount = parseReviewCount(
      item?.review_count ?? item?.reviews ?? item?.rating_count ?? item?.reviewCount
    );

    return {
      platform,
      product_id: String(item?.product_id ?? item?.pid ?? item?.sku ?? "").trim(),
      price: Number.isFinite(price) ? price : 0,
      offer_price: Number.isFinite(offerPrice) ? offerPrice : 0,
      url: String(pickVisitUrl(item) || "").trim(),
      product_name: String(item?.product_name ?? item?.title ?? "").trim(),
      match_type: String(item?.match_type ?? item?.matchType ?? "").trim(),
      rating: Number.isFinite(rating) ? rating : 0,
      review_count: Number.isFinite(reviewCount) ? reviewCount : 0,
      brand: String(item?.brand ?? product?.brand ?? "").trim(),
      description: String(item?.description ?? product?.description ?? "").trim(),
    };
  });
}

function lowestPricedPlatformRow(platformData) {
  const priced = (Array.isArray(platformData) ? platformData : []).filter(
    (row) => Number.isFinite(row?.price) && row.price > 0
  );
  if (!priced.length) return null;
  return priced.reduce((best, row) => (row.price < best.price ? row : best));
}

/** Enrich paste-list / catalog products with full 4-store platform_data for basket calc. */
export function preparePasteListProductsForBasket(products) {
  return (Array.isArray(products) ? products : []).map((product) => {
    const platform_data = buildGroceryPlatformData(product);
    return {
      ...product,
      platform_data,
      comparison: platformDataToComparison(platform_data),
    };
  });
}

export function buildGroceryAddToListBody(product, { quantity = 1 } = {}) {
  const productId = product?.product_id ?? product?.pid ?? product?.sku ?? null;
  const platform_data = buildGroceryPlatformData(product);
  const lowest = lowestPricedPlatformRow(platform_data);

  return {
    product_id: String(productId),
    product_name: product?.title || product?.product_name || product?.name || product?.sku || "",
    category: product?.category || product?.category_name || undefined,
    subcategory:
      product?.subcategory || product?.sub_category || product?.subCategory || undefined,
    platform:
      lowest?.platform ||
      normalizeGroceryProductPlatform(product?.platform) ||
      undefined,
    image_url: product?.images?.[0] || product?.image || product?.image_url || "",
    price: lowest?.price ?? parsePrice(product?.price) ?? null,
    description: lowest?.description || product?.description || undefined,
    brand: lowest?.brand || product?.brand || undefined,
    weight: product?.packsize || product?.weight || "",
    quantity,
    platform_data,
  };
}
